import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Button
} from 'reactstrap';
import axios from 'axios';
import api from '../../config.js';
import aboutImage2 from '../assets/images/about-apexcxos-2.png';
import faviconIcon from '../assets/images/favicon.png';

const OurValue = () => {
  // State management  
  const [valuesData, setValuesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Static fallback data
  const staticValues = [
    {
      id: 1,
      title: 'Empowerment',
      description: 'Equipping CISOs, CIOs, and their C-suite peers to lead with confidence in a complex digital world.'
    },
    {
      id: 2,
      title: 'Collaboration',
      description: 'Building trusted peer-to-peer connections that fuel shared success.'
    },
    {
      id: 3,
      title: 'Integrity',
      description: 'Championing transparency, trust, and ethical leadership in cybersecurity.'
    },
    {
      id: 4,
      title: 'Innovation',
      description: 'Inspiring bold thinking to stay ahead of evolving threats and opportunities.'
    }
  ];

  // Fetch Our Values data from CMS
  const fetchValuesData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get('/api/public/cms/our-values');
      
      if (response.data.isOk) {
        setValuesData(response.data.data);
      } else {
        setError('Failed to fetch our values data');
      }
    } catch (error) {
      console.error('Error fetching our values data:', error);
      setError('Error loading our values content');
      setValuesData(null); // Fallback to static content
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchValuesData();
  }, []);

  // Loading state
  if (loading) {
    return (
      <section className="values-section">
        <Container>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading our values...</span>
            </div>
          </div>
        </Container>
      </section>
    );
  }

  // Get current data (dynamic if available, fallback to static)
  const currentData = valuesData || {
    heading1: "Together, We Go Beyond Leadership",
    heading2: "Our Values", 
    heading3: "We're not just executives with titles. We are builders of cyber resilience, enablers of innovation, and champions of positive change.",
    description1: "Our mission is clear: to empower cybersecurity and technology leaders with the community, knowledge, and influence they need to make lasting impact across industries worldwide.",
    points: staticValues.map(value => ({
      subHeading: value.title,
      description: value.description
    }))
  };

  // Use dynamic points if available, otherwise fallback to static values
  const valuesToDisplay = currentData.points && currentData.points.length > 0 
    ? currentData.points 
    : staticValues.map(value => ({
        subHeading: value.title,
        description: value.description
      }));

  return (
    <section className="values-section">
      <Container>
        <Row>
          
          <Col lg={6} className="mb-4 mb-lg-0">
            <p className="text-uppercase small about-subtitle">
              Together, We Go Beyond Leadership
            </p>
            <h2 className="mb-3 about-title">
              {currentData.heading1 || "Our Values"}
            </h2>
            <h2 className="mb-4">
              {currentData.heading2 || (
                <>
                  We're not just executives with titles.<br className="d-none d-lg-block" />
                  We are builders of cyber resilience, enablers 
                  of innovation, and champions of positive change.
                </>
              )}
            </h2>

            <img 
              src={
                currentData.image 
                  ? `${api.API_URL}/${currentData.image}` 
                  : aboutImage2
              } 
              className="img-fluid rounded w-100" 
              alt="About ApexCXOs Values"
              onError={(e) => {
                // Fallback to static image if dynamic image fails to load
                e.target.src = aboutImage2;
              }}
            />
          </Col>

          <Col lg={6} className="ps-lg-5 our-mission">
            <p className="mt-3 mb-lg-4">
              {currentData.description1 || "Our mission is clear: to empower cybersecurity and technology leaders with the community, knowledge, and influence they need to make lasting impact across industries worldwide."}
            </p>
            
            {currentData.description2 && (
              <p className="mb-lg-4">
                {currentData.description2}
              </p>
            )}
            
            <Button 
              tag="a" 
              href="/partner-with-us" 
              className="btn-custom-black mb-3 mb-md-3 "
            >
              Partner With Us
            </Button>

            <div className="values-list">
              <ul className="list-unstyled">
                {valuesToDisplay.map((value, index) => (
                  <li 
                    key={index} 
                    className={`d-flex align-items-center ${index < valuesToDisplay.length - 1 ? 'mb-2' : ''}`}
                  >
                    <img 
                      src={faviconIcon} 
                      className="me-2" 
                      width="30" 
                      alt="toggle" 
                    />
                    <div>
                      <h4>{value.subHeading}</h4>
                      <p>{value.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </Col>

        </Row>
        
        {/* Show error message if data failed to load but don't break the UI */}
        {error && (
          <Row className="mt-3">
            <Col xs="12">
              <div className="alert alert-warning text-center" role="alert">
                <small>{error} - Displaying default content</small>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </section>
  );
};

export default OurValue;
